/**
 * お悩みテーマ（予約フォームのウィザード用）の写像モジュール。
 *
 * 値（type / sub 番号＝保存値）も表示文言も backend の master labels が SoT。本モジュールが
 * 持つのは「テーマカード ⇄ 保存 type/sub」の写像・STEP2 のグループ見出し・必須判定だけで、
 * 保存値も語彙も作り変えない。以前は分類語 → 日常語のオーバーレイ表を持っていたが、master を
 * 日常語へ統一した時点で完全冗長になったため撤去した（語の在り処は master 1 本）。
 *
 * カード定義が label / desc を保持しているのは master 未ロード時のフォールバック用。label は
 * cardLabel() が master 優先で解決するので実表示には出ないが、desc は master に対応物が無く
 * 定数がそのまま出る＝説明文を変えるときは master でなく本ファイルを直す。
 *
 * カードは master の大項目（type）と 1:1。表示上の分割・統合は行わないため、master に
 * サブが増えた場合も列挙を足さずに自動追従する。
 *
 * 純粋関数のみ（this 非依存・labels は引数渡し）。表示ゲート（is_corporate 企業のみ
 * ウィザードを出す）は呼び出し側の責務で、本モジュールは再ゲートしない。
 */

/**
 * テーマカード定義（STEP1 の選択肢＝6枚）。master の大項目 type と 1:1。
 * カード = type のため、選択カード → 保存 type は単純写像になり分割・重複は起きない。
 * label は master 未ロード時のフォールバック（実表示は cardLabel() が master 優先で解決）。
 * desc は master に対応物が無いため、ここが唯一の在り処になる。
 */
const THEME_CARDS = [
  {
    key: 'relations',
    type: 1,
    label: '職場の人間関係',
    desc: '上司・同僚・部下、孤立、ハラスメントなど',
  },
  {
    key: 'work',
    type: 2,
    label: '仕事の内容・進め方',
    desc: '業務量、難易度、指示、ミス、評価など',
  },
  {
    key: 'careerLife',
    type: 3,
    label: '雇用・キャリア・暮らし',
    desc: '契約、雇用不安、賃金、今後の働き方、家族、介護、金銭、住居など',
  },
  {
    key: 'traits',
    type: 4,
    label: '特性・職場の配慮',
    desc: '合理的配慮、勤務時間・休憩、通院、特性の開示など',
  },
  {
    key: 'health',
    type: 5,
    label: 'こころ・からだ',
    desc: '不安、気分、疲れ、睡眠、身体症状など',
  },
  {
    key: 'other',
    type: 6,
    label: 'その他',
    desc: '選択肢にない内容',
  },
];

/** サブを持たない大項目（その他）。自由記述で受けるため呼び出し側の必須判定に使う。 */
const OTHER_TYPE = 6;

/**
 * STEP2 のサブ項目を見出しで束ねる定義（type → グループ）。
 * カードを大項目と 1:1 にした結果、type=3 のサブが 8 件と多くなるため、選びやすさを
 * グループ見出しで担保する（カードの分割ではなく STEP2 内の表示上の束ね）。
 * ここに載らない type は見出し無しの単一グループになる。
 */
const SUB_GROUPS = {
  3: [
    { label: '雇用・キャリア', subNums: [1, 2, 3, 4] },
    { label: '家族・暮らし', subNums: [5, 6, 7, 8] },
  ],
};

/**
 * master labels から指定ブロックのサブ項目マップを取り出す。
 * @param {object} labels helper.master.labels 相当
 * @param {number|string} typeNum
 * @param {string} block 'texts' | 'reserve'
 * @returns {object} { [subNum]: label }（未定義時は空オブジェクト）
 */
function subMapOf(labels, typeNum, block) {
  return labels?.[block]?.type_company_sub?.[typeNum] ?? {};
}

/**
 * カード定義を key から引く。
 * @param {string} cardKey
 * @returns {object|undefined}
 */
export function cardByKey(cardKey) {
  return THEME_CARDS.find((card) => card.key === cardKey);
}

/**
 * カードの表示ラベル。master の type_company が SoT で、未ロード時のみカード定義側の label へ
 * フォールバックする（STEP1 は master ロード前に描画されうるため、ここで空表示を作らない）。
 * @param {object} card カード定義（THEME_CARDS の要素）
 * @param {object} labels helper.master.labels 相当
 * @param {string} block labels.json のブロック名（'texts' | 'reserve'）
 * @returns {string} 表示ラベル（該当なしは ''）
 */
export function cardLabel(card, labels, block = 'texts') {
  return labels?.[block]?.type_company?.[card?.type] ?? card?.label ?? '';
}

/**
 * 選択されたカード key 配列 → 保存用の大項目 type 配列。
 * カード = type が 1:1 のため重複は起きない（番号昇順で返す）。
 * @param {Array<string>} cardKeys 選択中のカード key
 * @returns {Array<number>} 保存する type 番号（昇順）
 */
export function cardsToSelectItems(cardKeys) {
  if (!Array.isArray(cardKeys)) return [];
  return cardKeys
    .map((key) => cardByKey(key)?.type)
    .filter((type) => type !== undefined)
    .sort((a, b) => a - b);
}

/**
 * 指定カードに属するサブ項目を「番号＋表示ラベル」で返す。
 * 番号・ラベルとも master が SoT（master に無い番号は返さない）。
 * @param {string} cardKey
 * @param {object} labels helper.master.labels 相当
 * @param {string} block labels.json のブロック名（'texts' | 'reserve'）
 * @returns {Array<{subNum:number, label:string}>} 番号昇順（サブ無しカードは空配列）
 */
export function subEntriesForCard(cardKey, labels, block = 'texts') {
  const card = cardByKey(cardKey);
  if (!card) return [];
  const subMap = subMapOf(labels, card.type, block);
  return Object.keys(subMap)
    .map(Number)
    .sort((a, b) => a - b)
    .map((subNum) => ({
      subNum,
      label: subMap[subNum],
    }));
}

/**
 * 指定カードのサブ項目を STEP2 の表示グループに束ねて返す。
 * SUB_GROUPS の定義が無い type は見出し無しの単一グループ。定義があっても、そこに
 * 列挙されていない番号（master に後から増えたサブ）は末尾の見出し無しグループへ回し、
 * 表示から落ちないようにする（「master 追従で欠落しない」を維持）。
 * @param {string} cardKey
 * @param {object} labels helper.master.labels 相当
 * @param {string} block labels.json のブロック名（'texts' | 'reserve'）
 * @returns {Array<{label:string, entries:Array<{subNum:number, label:string}>}>} 空グループは含まない
 */
export function subGroupsForCard(cardKey, labels, block = 'texts') {
  const card = cardByKey(cardKey);
  const entries = subEntriesForCard(cardKey, labels, block);
  if (!entries.length) return [];
  const defs = SUB_GROUPS[card.type];
  if (!defs) return [{ label: '', entries }];
  const grouped = defs.map((def) => ({
    label: def.label,
    entries: entries.filter((entry) => def.subNums.includes(entry.subNum)),
  }));
  const ungrouped = entries.filter(
    (entry) => !defs.some((def) => def.subNums.includes(entry.subNum)),
  );
  if (ungrouped.length) grouped.push({ label: '', entries: ungrouped });
  return grouped.filter((group) => group.entries.length);
}

/**
 * 逆写像: 保存 type → 属するテーマカード。
 * 予約フォームの確認ステップは in-memory のカード選択をそのまま表示するため現状は未使用。
 * 履歴・支援者画面をカード語表示へ広げる場合の入口として export しておく
 * （カードと type が 1:1 なのでサブ番号を見る必要は無い）。
 * @param {number|string} typeNum
 * @returns {object|null} カード定義（該当なしは null）
 */
export function cardForType(typeNum) {
  const type = Number(typeNum);
  return THEME_CARDS.find((card) => card.type === type) ?? null;
}

/**
 * サブ必須判定（STEP2 の「次へ」・確認ステップ進入時の最終ガード共用）。
 * 「選択した各カードにチップ 1 つ以上」を要求する。カード = type が 1:1 のためカード軸と
 * type 軸は同値で、判定は本関数 1 本に畳んである。
 * サブを持たないカード（その他）は master にサブが無い＝対象外で、自由記述側で担保する。
 * @param {Array<string>} cardKeys 選択中のカード key
 * @param {object} selectSubItems 保存形のサブ選択 { [typeNum]: [subNum, ...] }
 * @param {object} labels helper.master.labels 相当
 * @param {string} block labels.json のブロック名
 * @returns {Array<string>} チップ未選択のカード key（空なら OK）
 */
export function missingSubCards(cardKeys, selectSubItems, labels, block = 'texts') {
  if (!Array.isArray(cardKeys)) return [];
  return cardKeys.filter((cardKey) => {
    const entries = subEntriesForCard(cardKey, labels, block);
    if (!entries.length) return false;
    const card = cardByKey(cardKey);
    const selected = (selectSubItems?.[card.type] ?? []).map(Number);
    return !entries.some((entry) => selected.includes(entry.subNum));
  });
}

export { THEME_CARDS, OTHER_TYPE };
